/**
 * Imports the necessary components and libraries for rendering a smart table
 * with translation support using React.
 * @module React
 * @module CSmartTable
 * @module useTranslation
 * @module Card
 * @requires "@coreui/react-pro"
 * @requires "react-i18next"
 * @requires "react-bootstrap"
 */
import { CSmartTable } from "@coreui/react-pro";
import React from "react";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";

import CustomLoadingHTML from "../../../../components/customLoadingHTML/customLoadingHTML";
import EmptyCell from "../../../../components/emptyCell/emptyCell";
import {
  getCloudinaryMediaBaseUrl,
  getCloudinaryMobileDeliveryBase,
} from "../../../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc } from "../../../../helpers/helper.service";
import AvatarImageWithFallback from "../../../../components/avatarImageWithFallback/AvatarImageWithFallback";
import ConfirmModal from "../../../confirmModal/confirmModel";

const TABLE_BODY_MIN_SCROLL_PX = 280;
const TABLE_BODY_MAX_SCROLL_PX = 420;

const InQueueTabComponent = (props) => {
  /**
   * Destructures the necessary props from the `props` object and assigns them to
   * individual variables for easier access and readability.
   * @param {{object}} props - The props object containing all the necessary data.
   * @returns None
   */
  const { t } = useTranslation();
  const {
    inQueueData,
    columns,
    placeHolderShimer,
    noDataTitle,
    noDataDesc,
    clearSearchButton,
    smarttableref,
    noDataFound,
    noDataAddButtonTitle,
    emptyDataAddButton,

    dimensions,
    breadcrumbHeight,
    loading,
    tableContainerRef,
    loaderHtml,
    emptyImg,
    emptyTitle,
    showViewTicketModal,
    onViewTicket,
    closeViewTicketModal,
    itemsPerPage,
    emptyDataDescTow,
  } = props;

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  const CLOUDINARY_URL_MOBILE = getCloudinaryMobileDeliveryBase();
  const breadcrumbOffset = Number(breadcrumbHeight) || 0;
  const loaderOffset = loading && loaderHtml ? Number(loaderHtml) : 0;
  const viewportGap = dimensions.width > 1279 ? 150 : 250;
  const tableBodyScrollHeight = Math.max(
    TABLE_BODY_MIN_SCROLL_PX,
    Math.min(
      TABLE_BODY_MAX_SCROLL_PX,
      dimensions.height - breadcrumbOffset - loaderOffset - viewportGap
    )
  );

  return (
    <React.Fragment>
      <div className="inQueueTab">
        <div className="inQueue">
          <Card className="mainCard mainCardWrapper">
            <Card.Body className="p-0 activeTab">
              <div className="mainCardBody">
                <CSmartTable
                  ref={smarttableref}
                  cleaner
                  clickableRows
                  columns={columns}
                  columnFilter={false}
                  items={Array.isArray(inQueueData) ? inQueueData : []}
                  itemsPerPage={itemsPerPage}
                  scopedColumns={{
                    userid: (item) => {
                      const userImage = item?.userid?.image;
                      const profileSrc = userImage
                        ? resolveCloudinaryImageSrc(
                            userImage,
                            CLOUDINARY_URL_MOBILE || CLOUDINARY_URL
                          )
                        : "";
                      return (
                        <td>
                          <div className="tdWrapper profilePic">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <AvatarImageWithFallback
                              src={profileSrc}
                              alt={item?.userid?.fullname || "profile"}
                              width={32}
                              height={32}
                            />
                            {item?.userid?.fullname}
                          </div>
                        </td>
                      );
                    },
                  }}
                  tableProps={{
                    className: "mainTable",
                    responsive: true,
                    striped: false,
                    hover: false,
                  }}
                  noItemsLabel={
                    !placeHolderShimer && (
                      <EmptyCell
                        emptyItems={inQueueData}
                        noDataTitle={
                          inQueueData?.length === 0 ? noDataFound : noDataTitle
                        }
                        emptyImg={emptyImg}
                        emptyTitle={emptyTitle}
                        noDataAddButton={true}
                        noDataAddButtonTitle={noDataAddButtonTitle}
                        noDataDesc={noDataDesc}
                        emptyDataAddButton={emptyDataAddButton}
                        clearSearchButton={clearSearchButton}
                        emptyDataDescTow={emptyDataDescTow}
                      />
                    )
                  }
                  tableBodyProps={{
                    className: "align-middle Table-Body-scroll main",
                    style: {
                      height: tableBodyScrollHeight,
                    },
                    ref: tableContainerRef,
                  }}
                />
                {loading && <CustomLoadingHTML />}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <ConfirmModal
        className="viewTicketModal"
        isModelIcon={false}
        isCloseBtn={true}
        closeModelIcon={() => closeViewTicketModal()}
        isUnderline={false}
        showModal={showViewTicketModal}
        isFirstButton={false}
        isSecondButton={false}
        onProcced={onViewTicket}
        rightButtonClick={closeViewTicketModal}
        modalContent={false}
        badgeDate={true}
        modalTitleDiv={false}
      >
        <div className="viewTicketBody">
          <div className="bodyUpper">
            <span className="ticketTitle">The Weekend</span>
            <span className="ticketId">
              {t("TICKETID")}: ABCDE-0011AA-00223F
            </span>
            <span className="ticketTime">
              {t("EVENT_TIMINGS")}: 07 Oct 23, 10:00 AM
            </span>
            <span className="ticketBarcode">
              <img src={CLOUDINARY_URL + "barcode.png"} alt="barcode" />
            </span>
          </div>
          <div className="bodyBottom">
            <div className="pricewrapper">
              <span className="bottomlabel">{t("TICKET_PRICE")}</span>
              <span className="bottomValue">4 x ₹ 20.99</span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel">{t("TICKET_PRICE")}</span>
              <span className="bottomValue">+ ₹ 20.00</span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel totalAmount">
                {t("TOTAL_AMOUNT")}t
              </span>
              <span className="bottomValue totalAmount">₹ 105.96</span>
            </div>
          </div>
        </div>
      </ConfirmModal>
    </React.Fragment>
  );
};

export default InQueueTabComponent;
